import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authService } from "@/services/authService";
import { tokenStore } from "@/services/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      setUser(await authService.me());
    } catch {
      tokenStore.clear();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (identifier, password) => {
    const result = await authService.login(identifier, password);
    if (result.user) setUser(result.user);
    return result;
  }, []);

  const completeLogin = useCallback((u) => setUser(u), []);

  const register = useCallback(async (payload) => {
    const u = await authService.register(payload);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, completeLogin, register, logout, refresh: bootstrap }),
    [user, loading, login, completeLogin, register, logout, bootstrap],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
